/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/useScopeAgPersistHandlers.ts
 * FILE_ID: QB-AGGRID-PERSIST-HANDLERS-001
 * PURPOSE: All persistence-side callbacks for the scope AG-Grid section —
 *          cell edits, range pastes, fill-down, pad promotion. Pulled out
 *          so ScopeAgGridSection stays under the 300-line cap (rule 1.7).
 * WHY: Each handler is a small I→O transform: AG-Grid event → backend
 *      patch / create call. Keeping them in their own module makes them
 *      independently testable and lets the section component focus on
 *      orchestration (layout + state).
 * FLOW:
 *   ┌──────────────────┐   ┌────────────────────┐   ┌──────────────────┐
 *   │ AG-Grid event    │ → │ handler() in here  │ → │ onAdd/onUpdate / │
 *   │ (cell change,    │   │ classifies real vs │   │ onPasteWithCreate│
 *   │ paste, fill)     │   │ pad, dispatches    │   │ from parent      │
 *   └──────────────────┘   └────────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - pad-row-helpers (QB-AGGRID-PAD-ROWS-001)
 * RELATED:
 *   - ScopeAgGridSection (QB-AGGRID-SECTION-001) — consumer
 */

import { useCallback } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import {
  buildPromotePayload,
  isMeaningfulPadEdit,
  isPadRowId,
  type PadRowSeed,
} from './pad-row-helpers'
import { buildFormulaCellPatch } from './formula-patch'
import {
  CURRENCY_FIELDS,
  NUMERIC_FIELDS,
  PERCENT_FIELDS,
} from './scope-ag-grid-formatters'

/**
 * I→O. True when `field` is a Decimal/Number column on the backend
 * (currency, plain numeric, or percent). Drives the formula-patch
 * helper's "unparseable text → null vs passthrough" decision so a
 * stray paste doesn't write 'N/A' into adjusted_rate.
 *
 * Custom columns route through their own `custom_values` path and
 * never reach this code, so we only need the three base sets.
 */
function isNumericFieldId(field: string): boolean {
  return (
    NUMERIC_FIELDS.has(field) ||
    CURRENCY_FIELDS.has(field) ||
    PERCENT_FIELDS.has(field)
  )
}

/**
 * I→O. The display layer renders percent fields as user-friendly
 * percentages (10 means 10%) but the backend stores them as
 * fractions (0.10). When a percent cell is edited we therefore have
 * to divide the user's input by 100 BEFORE the patch is built, so a
 * "15" lands as 0.15 on the model.
 *
 * Returns the value unchanged for non-percent fields. Null/undefined
 * pass through untouched so "clear the override" still works.
 */
function normalisePercentForBackend(field: string, value: unknown): unknown {
  if (!PERCENT_FIELDS.has(field)) return value
  if (value === null || value === undefined || value === '') return value
  // Strings carry through as-is so the formula path (`=A1*2`) is
  // still detected downstream — only literal numbers / numeric
  // strings need the /100 transform.
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return value
    return value / 100
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '' || trimmed.startsWith('=')) return value
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return value
    return n / 100
  }
  return value
}

export interface UseScopeAgPersistHandlersArgs {
  sectionId: string
  rows: QuoteLineItem[]
  displayRows: QuoteLineItem[]
  editableFieldIds: string[]
  isEditable: boolean
  padSeed: PadRowSeed
  onAddItem: (
    sectionId: string,
    data: Record<string, unknown>,
  ) => void
  onUpdateItem: (
    sectionId: string,
    itemId: string,
    patch: Partial<QuoteLineItem>,
  ) => void
  onPasteWithCreate?: (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number,
  ) => Promise<void>
}

export interface ScopeAgPersistHandlers {
  handleCellEdit: (itemId: string, field: string, value: unknown) => void
  handlePadPromote: (padId: string, field: string, value: unknown) => void
  handleFillDown: (
    sourceItemId: string,
    targetItemIds: string[],
    fields: string[],
  ) => void
  handlePasteRange: (
    startItemId: string,
    startField: string,
    cells: string[][],
  ) => void
}

export function useScopeAgPersistHandlers(
  args: UseScopeAgPersistHandlersArgs,
): ScopeAgPersistHandlers {
  const {
    sectionId,
    rows,
    displayRows,
    editableFieldIds,
    isEditable,
    padSeed,
    onAddItem,
    onUpdateItem,
    onPasteWithCreate,
  } = args

  const handleCellEdit = useCallback(
    (itemId: string, field: string, value: unknown) => {
      // Route every single-cell edit through buildFormulaCellPatch so
      // estimators' "=qty * 1.1" typing is interpreted as a formula
      // (evaluated + the raw expression persisted to `formulas[field]`)
      // rather than landing as a literal string on a Decimal column.
      // Falls back to "single field write" for plain numbers — no
      // behaviour change vs the pre-formula world. Text fields
      // (description, location, ...) preserve their raw string via
      // the helper's isNumericField=false branch.
      const row = rows.find((r) => r.id === itemId)
      // MR H: percent fields display as 10 but persist as 0.10. The
      // /100 transform happens here (NOT in buildFormulaCellPatch)
      // because formulas like "=A1*2" need to stay un-normalised on
      // their way into the evaluator — only literal user typing gets
      // converted.
      const normalisedValue = normalisePercentForBackend(field, value)
      if (!row) {
        // Row gone? Best we can do is forward the raw value so the
        // backend can decide. Should be rare (row was deleted mid-edit).
        onUpdateItem(sectionId, itemId, {
          [field]: normalisedValue as QuoteLineItem[keyof QuoteLineItem],
        })
        return
      }
      const { patch } = buildFormulaCellPatch(
        row,
        field,
        normalisedValue as never,
        {
          isNumericField: isNumericFieldId(field),
        },
      )
      onUpdateItem(sectionId, itemId, patch)
    },
    [rows, onUpdateItem, sectionId],
  )

  // Pad promotion: user committed a non-blank cell on a pad. Build a
  // minimal create payload + the typed field, dispatch onAddItem. Next
  // render swaps the pad for the real row, a fresh pad fills the slot.
  const handlePadPromote = useCallback(
    (padId: string, field: string, value: unknown) => {
      if (!isEditable) return
      if (!isPadRowId(padId)) return
      if (!isMeaningfulPadEdit(field, value)) return
      onAddItem(sectionId, buildPromotePayload(padSeed, field, value))
    },
    [isEditable, padSeed, onAddItem, sectionId],
  )

  // Fill-down: skip pad rows on both ends so we never silently overwrite
  // pad seeds without promoting them, and never propagate "blank" from a
  // pad source. SAM follows the same rule.
  const handleFillDown = useCallback(
    (sourceItemId: string, targetItemIds: string[], fields: string[]) => {
      if (isPadRowId(sourceItemId)) return
      const source = rows.find((r) => r.id === sourceItemId)
      if (!source) return
      for (const targetId of targetItemIds) {
        if (isPadRowId(targetId)) continue
        const patch: Partial<QuoteLineItem> = {}
        for (const f of fields) {
          ;(patch as Record<string, unknown>)[f] = (
            source as unknown as Record<string, unknown>
          )[f]
        }
        onUpdateItem(sectionId, targetId, patch)
      }
    },
    [rows, onUpdateItem, sectionId],
  )

  // Paste range: edits land on real rows via onUpdateItem; any rows past
  // the real list (i.e. cells dropped on pads or rows beyond the pads)
  // become "overflow" and route through onPasteWithCreate which already
  // knows how to bulk-create.
  const handlePasteRange = useCallback(
    (startItemId: string, startField: string, cells: string[][]) => {
      const startIdxInDisplay = displayRows.findIndex((r) => r.id === startItemId)
      if (startIdxInDisplay < 0) return
      const fieldStartIdx = editableFieldIds.indexOf(startField)
      if (fieldStartIdx < 0) return

      const updates: Array<{ itemId: string; field: string; value: string }> = []
      const overflow: Array<Record<string, unknown>> = []

      for (let r = 0; r < cells.length; r++) {
        const targetIdx = startIdxInDisplay + r
        const cellsRow = cells[r] ?? []
        const overflowRow: Record<string, unknown> = {}
        for (let c = 0; c < cellsRow.length; c++) {
          const fieldIdx = fieldStartIdx + c
          if (fieldIdx >= editableFieldIds.length) break
          const field = editableFieldIds[fieldIdx]
          const value = cellsRow[c] ?? ''
          if (targetIdx < rows.length) {
            updates.push({ itemId: rows[targetIdx].id, field, value })
          } else {
            overflowRow[field] = value
          }
        }
        if (Object.keys(overflowRow).length > 0) overflow.push(overflowRow)
      }

      for (const u of updates) {
        onUpdateItem(sectionId, u.itemId, {
          [u.field]: u.value as QuoteLineItem[keyof QuoteLineItem],
        })
      }

      if (overflow.length > 0 && onPasteWithCreate) {
        void onPasteWithCreate(sectionId, rows, overflow, rows.length)
      }
    },
    [displayRows, rows, editableFieldIds, onUpdateItem, onPasteWithCreate, sectionId],
  )

  return { handleCellEdit, handlePadPromote, handleFillDown, handlePasteRange }
}
